import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { apiFetch } from '../lib/apiFetch';

/** For gift cards and gift passes `tickets` counts the cards or passes sold, and for
 *  retail the items sold; `bookings` counts the orders. */
export interface SalesTotals {
  bookings: number;
  tickets: number;
  grossCents: number;
  subtotalCents: number;
}

export interface DailySalesDay {
  /** Eastern-time calendar date, YYYY-MM-DD. */
  date: string;
  /** Null when the warehouse could not be read. */
  fareharbor: SalesTotals | null;
  /** Null when the warehouse could not be read. */
  giftCards: SalesTotals | null;
  nebari: SalesTotals;
  /** Gift passes bought through Nebari. */
  giftPasses: SalesTotals;
  /** Null when the warehouse could not be read. */
  retailInStore: SalesTotals | null;
  /** Null when the warehouse could not be read. */
  retailOnline: SalesTotals | null;
}

export interface SalesRegion {
  id: string;
  name: string;
  parentId: string | null;
}

export interface RegionSalesRow {
  /** Null for sales whose workshop has no region. */
  regionId: string | null;
  name: string;
  fareharbor: SalesTotals | null;
  nebari: SalesTotals;
}

/** Eastern-time calendar dates, YYYY-MM-DD, both included. */
export interface SalesDateRange {
  from: string;
  to: string;
}

export interface DailySalesReport extends SalesDateRange {
  today: string;
  /** The day the single-day summary figures describe: yesterday when the range runs to
   *  today, otherwise its last day. */
  kpiDay: string;
  /** The same weekday a week before `kpiDay`, even when it falls before `from`. */
  kpiWeekBefore: DailySalesDay | null;
  generatedAt: string;
  /** The newest FareHarbor sale in the window, not when the warehouse last refreshed. */
  fareharborLatestSale: string | null;
  nebariLatestSale: string | null;
  giftPassLatestSale: string | null;
  /** The newest retail sale on or before `to`; long before today means the Shopify copy
   *  has stopped updating. */
  retailLatestSale: string | null;
  /** The region workshop tickets are narrowed to; null for every region. */
  region: SalesRegion | null;
  regionOptions: SalesRegion[];
  /** Ticket sales for each region one level inside `region`, largest first. */
  regionSales: RegionSalesRow[];
  days: DailySalesDay[];
}

/** Which part of the query a 400 refused. */
export type RefusedField = 'dates' | 'region';

export class DailySalesError extends Error {
  constructor(
    readonly status: number,
    readonly serverMessage?: string,
    readonly field: RefusedField = 'dates',
  ) {
    super(DailySalesError.messageFor(status, serverMessage, field));
  }

  private static messageFor(status: number, serverMessage?: string, field?: RefusedField): string {
    if (status === 400 && field === 'region') {
      return "That region link isn't valid. Show all regions, or pick one from the list.";
    }
    if (status === 400 && serverMessage) {
      return `Those dates can't be shown: ${serverMessage}.`;
    }
    if (status === 404) {
      return 'That region no longer exists. Show all regions, or pick another one.';
    }
    if (status === 403) {
      return 'Your role does not include the Daily sales report. Ask an admin if you need it.';
    }
    return 'Daily sales could not be loaded. Try again in a minute.';
  }

  get isRefusedDates(): boolean {
    return this.status === 400 && this.field === 'dates';
  }

  /** The chosen region was malformed or no longer exists, so only another region helps. */
  get isRefusedRegion(): boolean {
    return this.status === 404 || (this.status === 400 && this.field === 'region');
  }

  /** The server's reason for refusing the dates, as a sentence; null for other failures. */
  get refusedReason(): string | null {
    if (!this.isRefusedDates || !this.serverMessage) return null;
    return `${this.serverMessage.charAt(0).toUpperCase()}${this.serverMessage.slice(1)}.`;
  }
}

async function fetchDailySales(
  range: SalesDateRange | null,
  regionId: string | null,
): Promise<DailySalesReport> {
  const params = new URLSearchParams(range ? { from: range.from, to: range.to } : {});
  if (regionId) params.set('region', regionId);
  const query = params.toString() ? `?${params}` : '';
  const res = await apiFetch(`/api/v1/finance/daily-sales${query}`);
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as {
      message?: string;
      field?: RefusedField;
    } | null;
    throw new DailySalesError(res.status, body?.message, body?.field);
  }
  const body = (await res.json()) as { data: DailySalesReport };
  return body.data;
}

/** The report for `range` (the default window when null), with tickets narrowed to
 *  `regionId` when one is given. The previous report stays in place while a new one loads. */
export function useDailySales(range: SalesDateRange | null, regionId: string | null = null) {
  return useQuery({
    queryKey: ['finance', 'daily-sales', range?.from ?? null, range?.to ?? null, regionId],
    queryFn: () => fetchDailySales(range, regionId),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    retry: (count, error) =>
      !(error instanceof DailySalesError && error.status >= 400 && error.status < 500) && count < 2,
  });
}
